import { apiClient } from '@/api/client';
import { Business } from '@/types/business';
import { Paginated } from '@/types/pagination';
import { mapBusinessWireToDomain } from '../mappers/business';
import { mapPhotoMetaWire } from '../mappers/photo';
import type { PhotoMetaWire } from '../schemas/photo';
import {
  businessWireSchema,
  paginatedBusinessResponseSchema,
  type BusinessWire,
  type PaginatedBusinessResponseWire,
} from '../schemas/business';

/**
 * Domain-oriented API module for business operations
 * Provides typed functions for fetching businesses from the Gateway API
 */

/**
 * Query parameters for listing businesses
 */
export interface ListBusinessesParams {
  city?: string;
  state?: string;
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'rating' | 'created_at';
  sort_order?: 'asc' | 'desc';
  status?: 'active' | 'inactive' | 'pending';
}

/**
 * Query parameters for searching businesses
 */
export interface SearchBusinessesParams {
  query?: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  status?: string;
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'rating' | 'created_at' | 'relevance';
  sort_order?: 'asc' | 'desc';
}

/**
 * Query parameters for nearby businesses
 */
export interface NearbyBusinessesParams {
  latitude: number;
  longitude: number;
  radius_km?: number;
  limit?: number;
  status?: string;
}

/**
 * Domain nearby businesses response.
 * Each item is a domain Business with `distanceKm` populated.
 */
export interface NearbyBusinessResponse {
  items: Business[];
  total: number;
}

/**
 * Wire shape for a single nearby business (domain mapped at the seam).
 */
interface NearbyBusinessWire extends BusinessWire {
  distance_km: number;
}

/**
 * Map a paginated wire response to a domain Paginated<Business>.
 */
function mapPaginatedBusinessWireToDomain(
  wire: PaginatedBusinessResponseWire,
): Paginated<Business> {
  return {
    items: wire.items.map(mapBusinessWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Build query string from parameters
 */
function buildQueryString(params: ListBusinessesParams): string {
  const queryParams: string[] = [];

  if (params.city) {
    queryParams.push(`city=${encodeURIComponent(params.city)}`);
  }
  if (params.state) {
    queryParams.push(`state=${encodeURIComponent(params.state)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }
  if (params.status) {
    queryParams.push(`status=${params.status}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * Build query string from search parameters
 */
function buildSearchQueryString(params: SearchBusinessesParams): string {
  const queryParams: string[] = [];

  if (params.query) {
    queryParams.push(`query=${encodeURIComponent(params.query)}`);
  }
  if (params.city) {
    queryParams.push(`city=${encodeURIComponent(params.city)}`);
  }
  if (params.state) {
    queryParams.push(`state=${encodeURIComponent(params.state)}`);
  }
  if (params.status) {
    queryParams.push(`status=${encodeURIComponent(params.status)}`);
  }
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }
  if (params.latitude !== undefined) {
    queryParams.push(`latitude=${params.latitude}`);
  }
  if (params.longitude !== undefined) {
    queryParams.push(`longitude=${params.longitude}`);
  }
  if (params.sort_by) {
    queryParams.push(`sort_by=${params.sort_by}`);
  }
  if (params.sort_order) {
    queryParams.push(`sort_order=${params.sort_order}`);
  }

  return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
}

/**
 * List businesses with optional filtering and pagination
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of businesses
 *
 * @example
 * ```typescript
 * // Default: Bloomington, IN
 * const result = await listBusinesses();
 *
 * // Custom city/state
 * const result = await listBusinesses({ city: 'San Francisco', state: 'CA' });
 *
 * // With pagination
 * const result = await listBusinesses({ page: 2, per_page: 10 });
 * ```
 */
export async function listBusinesses(
  params: ListBusinessesParams = {}
): Promise<Paginated<Business>> {
  // Default to Bloomington, IN if no city/state provided
  const queryParams: ListBusinessesParams = {
    city: 'Bloomington',
    state: 'IN',
    page: 1,
    per_page: 20,
    sort_by: 'name',
    sort_order: 'asc',
    ...params,
  };

  const queryString = buildQueryString(queryParams);
  const endpoint = `/api/business${queryString}`;

  const wire = await apiClient.get<unknown>(endpoint);
  const validated = paginatedBusinessResponseSchema.parse(wire);
  return mapPaginatedBusinessWireToDomain(validated);
}

/**
 * Search businesses with text query and optional filters
 *
 * @param params - Search parameters including query text, location filters, and pagination
 * @returns Paginated list of businesses matching the search
 *
 * @example
 * ```typescript
 * // Search for "pizza" in Bloomington, IN
 * const result = await searchBusinesses({
 *   query: 'pizza',
 *   city: 'Bloomington',
 *   state: 'IN',
 *   per_page: 5
 * });
 * ```
 */
export async function searchBusinesses(
  params: SearchBusinessesParams = {}
): Promise<Paginated<Business>> {
  const queryParams: SearchBusinessesParams = {
    page: 1,
    per_page: 20,
    sort_by: 'relevance',
    sort_order: 'asc',
    ...params,
  };

  const queryString = buildSearchQueryString(queryParams);
  const endpoint = `/api/business/search${queryString}`;

  const wire = await apiClient.get<unknown>(endpoint);
  const validated = paginatedBusinessResponseSchema.parse(wire);
  return mapPaginatedBusinessWireToDomain(validated);
}

/**
 * Get nearby businesses by location
 *
 * @param params - Nearby search parameters including latitude, longitude, radius, and limit
 * @returns List of nearby businesses with distance
 *
 * @example
 * ```typescript
 * const result = await getNearbyBusinesses({
 *   latitude: 39.1653,
 *   longitude: -86.5264,
 *   radius_km: 5,
 *   limit: 20
 * });
 * ```
 */
export async function getNearbyBusinesses(
  params: NearbyBusinessesParams
): Promise<NearbyBusinessResponse> {
  const queryParams: string[] = [];

  queryParams.push(`latitude=${params.latitude}`);
  queryParams.push(`longitude=${params.longitude}`);

  if (params.radius_km !== undefined) {
    queryParams.push(`radius_km=${params.radius_km}`);
  }
  if (params.limit !== undefined) {
    queryParams.push(`limit=${params.limit}`);
  }
  if (params.status) {
    queryParams.push(`status=${encodeURIComponent(params.status)}`);
  }

  const queryString = `?${queryParams.join('&')}`;
  const endpoint = `/api/business/nearby${queryString}`;

  const wire = await apiClient.get<{ items: NearbyBusinessWire[]; total: number }>(endpoint);

  return {
    items: wire.items.map((item) => ({
      ...mapBusinessWireToDomain(item),
      distanceKm: item.distance_km,
    })),
    total: wire.total,
  };
}

/**
 * Query parameters for trending businesses
 */
export interface TrendingBusinessesParams {
  limit?: number;
}

/**
 * Domain trending businesses response.
 * Each item is a plain domain Business; the wire `check_in_count` is dropped.
 */
export interface TrendingBusinessResponse {
  items: Business[];
  total: number;
}

/**
 * Wire shape for a single trending business (domain mapped at the seam).
 */
interface TrendingBusinessWire extends BusinessWire {
  check_in_count: number;
}

/**
 * Get trending businesses by check-in count
 *
 * @param params - Optional limit parameter
 * @returns List of trending domain businesses
 */
export async function getTrendingBusinesses(
  params: TrendingBusinessesParams = {}
): Promise<TrendingBusinessResponse> {
  const queryParams: string[] = [];

  if (params.limit !== undefined) {
    queryParams.push(`limit=${params.limit}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/business/trending${queryString}`;

  const wire = await apiClient.get<{ items: TrendingBusinessWire[]; total: number }>(endpoint);

  return {
    items: wire.items.map(mapBusinessWireToDomain),
    total: wire.total,
  };
}

/**
 * Get a single business by ID
 *
 * @param id - Business ID
 * @returns Business domain model
 *
 * @example
 * ```typescript
 * const business = await getBusinessById('business-123');
 * ```
 */
export async function getBusinessById(id: string): Promise<Business> {
  const endpoint = `/api/business/${id}`;
  const wireData = await apiClient.get<BusinessWire>(endpoint);

  // Validate response with zod schema
  const validatedData = businessWireSchema.parse(wireData);

  // Map wire format to domain format
  return mapBusinessWireToDomain(validatedData);
}

/**
 * Get unique cuisine types from all businesses
 *
 * @returns Array of unique cuisine type strings, sorted alphabetically
 *
 * @example
 * ```typescript
 * const cuisines = await getUniqueCuisineTypes();
 * // Returns: ['american', 'italian', 'japanese', 'mexican', ...]
 * ```
 */
export async function getUniqueCuisineTypes(): Promise<string[]> {
  const endpoint = '/api/business/cuisines';
  const response = await apiClient.get<{ cuisines: string[] }>(endpoint);
  return response.cuisines;
}

/**
 * Photo from a business check-in
 */
export interface BusinessPhoto {
  id: string;
  imageUrl: string;
  r2Key?: string;
  /** Stored WebP dimensions and alt text; null when unknown. */
  width?: number | null;
  height?: number | null;
  altText?: string | null;
  /** The check-in the photo came from, for the lightbox caption. */
  checkIn?: BusinessPhotoCheckIn;
}

/**
 * Check-in context attached to a business photo. The rating is null when the
 * check-in has none or the server's rating display mode hides it.
 */
export interface BusinessPhotoCheckIn {
  id: string;
  occurredAt: string | null;
  menuItemName: string | null;
  rating: { key: string; label: string } | null;
  user: {
    id: string | null;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
    isSentinel: boolean;
  };
}

interface BusinessPhotoCheckInWire {
  id: string;
  occurred_at: string | null;
  menu_item_name: string | null;
  rating: { key: string; label: string } | null;
  user: {
    id: string | null;
    username: string | null;
    display_name: string | null;
    avatar_url: string | null;
    is_sentinel: boolean;
  };
}

function mapBusinessPhotoCheckIn(wire: BusinessPhotoCheckInWire): BusinessPhotoCheckIn {
  return {
    id: wire.id,
    occurredAt: wire.occurred_at,
    menuItemName: wire.menu_item_name,
    rating: wire.rating,
    user: {
      id: wire.user.id,
      username: wire.user.username,
      displayName: wire.user.display_name,
      avatarUrl: wire.user.avatar_url,
      isSentinel: wire.user.is_sentinel,
    },
  };
}

/**
 * Paginated business photos response (domain format)
 */
export interface PaginatedBusinessPhotosResponse {
  items: BusinessPhoto[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

interface BusinessPhotosWire {
  items: ({
    id: string;
    image_url: string;
    r2_key?: string;
    /** Absent from gateways that predate the photo caption. */
    check_in?: BusinessPhotoCheckInWire;
  } & PhotoMetaWire)[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Get photos from check-ins at a business with pagination
 *
 * @param businessId - Business ID
 * @param params - Optional pagination params
 * @returns Paginated photos response
 */
export async function getBusinessPhotos(
  businessId: string,
  params?: { page?: number; per_page?: number },
): Promise<PaginatedBusinessPhotosResponse> {
  const queryParts: string[] = [];
  if (params?.page !== undefined) queryParts.push(`page=${params.page}`);
  if (params?.per_page !== undefined) queryParts.push(`per_page=${params.per_page}`);
  const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

  const endpoint = `/api/business/${businessId}/photos${queryString}`;
  const wire = await apiClient.get<BusinessPhotosWire>(endpoint);

  return {
    items: wire.items.map((p) => ({
      id: p.id,
      imageUrl: p.image_url,
      ...(p.r2_key ? { r2Key: p.r2_key } : {}),
      ...mapPhotoMetaWire(p),
      ...(p.check_in ? { checkIn: mapBusinessPhotoCheckIn(p.check_in) } : {}),
    })),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Request body for suggesting a new business
 */
export interface SuggestBusinessRequest {
  name: string;
  address: string;
  cuisine_type?: string;
}

/**
 * Suggest a new business
 *
 * Creates a business with pending status that needs admin approval.
 *
 * @param data - Suggestion data including name and address (required), cuisine_type (optional)
 * @returns Created business with pending status
 */
export async function suggestBusiness(data: SuggestBusinessRequest): Promise<Business> {
  const wireData = await apiClient.post<BusinessWire>('/api/business/suggest', data);
  const validatedData = businessWireSchema.parse(wireData);
  return mapBusinessWireToDomain(validatedData);
}
